"use client";

import { useEffect, useState } from "react";

import {
  hashBlob,
  readCachedDeploymentAsset,
  writeCachedDeploymentAsset,
} from "~/lib/deployment-asset-cache";
import { localDeploymentAssetPath } from "~/lib/deployment-assets";

const REMOTE_FETCH_TIMEOUT_MS = 12_000;

/**
 * Shows a bundled local asset immediately, then prefers a cached copy or the
 * remote asset when the server responds. Updates the cache when the remote file changes.
 */
export function useDeploymentAssetSrc(
  filename: string,
  remoteUrl: string,
): string {
  const localPath = localDeploymentAssetPath(filename);
  const [src, setSrc] = useState(localPath);

  useEffect(() => {
    if (!remoteUrl) return;

    let cancelled = false;
    let activeObjectUrl: string | null = null;

    const applyObjectUrl = (blob: Blob) => {
      if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
      activeObjectUrl = URL.createObjectURL(blob);
      setSrc(activeObjectUrl);
    };

    void (async () => {
      const cached = await readCachedDeploymentAsset(remoteUrl);
      if (cancelled) return;
      if (cached) applyObjectUrl(cached.blob);

      try {
        const response = await fetch(remoteUrl, {
          cache: "no-cache",
          signal: AbortSignal.timeout(REMOTE_FETCH_TIMEOUT_MS),
        });
        if (!response.ok) return;

        const blob = await response.blob();
        if (cancelled) return;

        const hash = await hashBlob(blob);
        if (cached?.hash === hash) return;

        await writeCachedDeploymentAsset(remoteUrl, blob, hash);
        if (!cancelled) applyObjectUrl(blob);
      } catch {
        // Keep bundled local path or last cached blob URL.
      }
    })();

    return () => {
      cancelled = true;
      if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
    };
  }, [remoteUrl, filename]);

  return src;
}
